import React, { forwardRef } from 'react';
import clsx from 'clsx';

/**
 * The app's one button.
 *
 * Two ways to use it:
 *
 *  - `variant` + `size`: a complete, standard button (height, padding, rounding
 *    and colours all supplied).
 *  - `tone`: colours only. Size, padding and rounding come from `className`, so
 *    buttons that each have their own shape keep it exactly, while their colours
 *    still come from this one list. Children are rendered as-is.
 *
 * Every colour here is a theme token, so all buttons follow the client's theme.
 * Text on brand-coloured backgrounds uses `text-on-primary`: white by default,
 * dark when a theme's primary colour is light.
 */

export const BUTTON_TONES = {
  /** Solid primary colour. */
  primary: 'bg-agentic-primary-blue text-on-primary',
  /** Solid primary colour that fades slightly on hover. */
  'primary-fade': 'bg-agentic-primary-blue text-on-primary hover:bg-agentic-primary-blue/90',
  /** Primary gradient with a lifted hover. */
  'primary-gradient': 'bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light text-on-primary hover:shadow-md',
  /** Brand gradient (secondary to primary). */
  'brand-gradient': 'bg-gradient-to-r from-c-805dc6 to-c-3683c6 text-on-primary',
  /** Solid blue action button. */
  blue: 'bg-blue-600 text-on-primary hover:bg-blue-700',
  /** Solid blue without a hover change. */
  'blue-static': 'bg-blue-600 text-on-primary',
  /** Light outline. */
  outline: 'border-gray-200 hover:bg-gray-50',
  /** Light outline with dark text. */
  'outline-strong': 'border-gray-200 text-gray-700 hover:bg-gray-50',
  /** Light outline with secondary text. */
  'outline-muted': 'border-gray-200 text-agentic-text-secondary hover:bg-gray-50',
  /** White button with a grey outline. */
  'outline-white': 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
  /** Light outline with red text (delete / remove). */
  'outline-danger': 'border-gray-200 text-red-500 hover:bg-gray-50',
  /** Red outline (destructive). */
  'danger-soft': 'border-red-200 text-red-600 hover:bg-red-50',
  /** Red text only. */
  'danger-text': 'text-red-600',
  /** Red text link that darkens on hover. */
  'danger-link': 'text-red-500 hover:text-red-700',
  /** Blue text link. */
  link: 'text-blue-600 hover:text-blue-700',
  /** Grey icon button with a soft hover background. */
  'icon-ghost': 'text-gray-400 hover:bg-gray-100 hover:text-gray-700',
  /** Secondary text that darkens on hover. */
  'muted-text': 'text-agentic-text-secondary hover:text-agentic-text-primary',
  /** Light outline with medium-grey text. */
  'outline-subtle': 'border-gray-200 text-gray-600 hover:bg-gray-50',
  /** Grey outline with a slightly darker hover. */
  'outline-gray': 'border-gray-300 hover:bg-gray-100',
  /** White floating button with a shadow. */
  floating: 'border-gray-200 bg-white shadow-md hover:bg-gray-50',
  /** Brand-coloured text only. */
  'brand-text': 'text-kv-agentic-blue',
  /** Muted icon/text that turns red on hover (remove / delete). */
  'danger-ghost': 'text-agentic-text-muted hover:bg-red-50 hover:text-red-500',
  /** White text, for buttons on dark or coloured surfaces. */
  'on-dark': 'text-white',
  /** No colours of its own: all colours come from `className`. */
  custom: '',
} as const;

export type ButtonTone = keyof typeof BUTTON_TONES;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  /** Colours only; see the note above. */
  tone?: ButtonTone;
}

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-base',
  lg: 'h-12 px-5 text-base',
};

const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-gradient-to-r from-kv-agentic-purple to-kv-agentic-blue text-on-primary shadow-sm hover:brightness-105',
  secondary:
    'border border-c-3683c6 bg-white text-c-1f1f1f hover:bg-c-f8fbff',
  ghost: 'border border-agentic-gray-200 bg-white text-agentic-text-primary hover:bg-agentic-gray-50',
  danger: 'border border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    tone,
    className,
    children,
    ...props
  }, ref) => {
    if (tone) {
      return (
        <button ref={ref} disabled={disabled} className={clsx(BUTTON_TONES[tone], className) || undefined} {...props}>
          {children}
        </button>
      );
    }

    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={clsx(
          'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kv-agentic-blue focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          sizeClasses[size],
          variantClasses[variant],
          className
        )}
        {...props}
      >
        {loading ? <Spinner className="h-4 w-4 border-2 border-current border-t-transparent" /> : null}
        <span>{children}</span>
      </button>
    );
  }
);

Button.displayName = 'Button';

/**
 * Loading spinner: a spinning ring whose size and colours come from
 * `className` (e.g. `h-4 w-4 border-2 border-current border-t-transparent`).
 */
export const Spinner: React.FC<{ className?: string; as?: 'span' | 'div' }> = ({ className, as: Tag = 'span' }) => (
  <Tag className={clsx('animate-spin rounded-full', className)} aria-hidden="true" />
);
